import React, { useEffect } from 'react'

import { Button, TextInput } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, string } from 'prop-types'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import MTCaptcha from 'Utils/MTCaptcha'

import { EMAIL_REGEX } from '../AuthStep/AuthStep'
import PasswordField from './Components/PasswordField/PasswordField'

const CAPTCHA_ID = 'ai-sign-in-login-captcha'

function PasswordStep({
  email,
  password,
  isSubmitting,
  isTwoFaRequired,
  twoFaCode,
  captchaEnabled,
  passwordError = '',
  showRequiredNotice = false,
  onEmailChange,
  onPasswordChange,
  onTwoFaCodeChange,
  onSubmit,
  onBack,
}) {
  const t = useTranslation()
  const canSubmit = isTwoFaRequired
    ? Boolean(twoFaCode.trim())
    : EMAIL_REGEX.test(email.trim()) && password.length > 0

  // The switch link keeps DOM focus on the auth step's node, so move focus to
  // the field the user came here to fill - mirroring AuthStep's autoFocus.
  useEffect(() => {
    const fieldId = isTwoFaRequired ? 'ai-sign-in-two-fa-code' : 'ai-sign-in-password'
    document.getElementById(fieldId)?.focus()
  }, [isTwoFaRequired])

  const handleSubmit = (event) => {
    event.preventDefault()
    if (canSubmit && !isSubmitting) {
      onSubmit()
    }
  }

  return (
    <form className="ai-sign-in-modal__password" onSubmit={handleSubmit} noValidate>
      <div className="ai-sign-in-modal__header-group ai-sign-in-modal__header-group--center">
        <p className="ai-sign-in-modal__title cgt-heading-300">
          {t(isTwoFaRequired ? 'signIn.password.twoFaTitle' : 'signIn.password.title')}
        </p>
        <p className="ai-sign-in-modal__subtitle cgt-body-md">
          {t(isTwoFaRequired ? 'signIn.password.twoFaDescription' : 'signIn.password.subtitle')}
        </p>
      </div>
      {!isTwoFaRequired && showRequiredNotice && (
        <p className="ai-sign-in-modal__password-notice cgt-body-base">
          {t('signIn.password.requiredNotice')}
        </p>
      )}
      {isTwoFaRequired ? (
        <TextInput
          id="ai-sign-in-two-fa-code"
          name="twoFaCode"
          size="medium"
          label={t('signIn.password.twoFaCodeLabel')}
          inputMode="numeric"
          autoComplete="one-time-code"
          value={twoFaCode}
          onChange={onTwoFaCodeChange}
          wrapperClassName="ai-sign-in-modal__field"
          isPilot
        />
      ) : (
        <>
          <TextInput
            id="ai-sign-in-password-email"
            name="email"
            inputType="email"
            size="medium"
            label={t('signIn.email.label')}
            value={email}
            onChange={onEmailChange}
            wrapperClassName="ai-sign-in-modal__field"
            autoComplete="username"
            isPilot
          />
          <PasswordField value={password} onChange={onPasswordChange} />
          <p className="ai-sign-in-modal__forgot-password">
            <a
              href="/users/password/new"
              className="ai-sign-in-modal__switch-link cgt-body-md-semibold cgt-link-primary-accented"
            >
              {t('signIn.password.forgotPassword')}
            </a>
          </p>
        </>
      )}
      {captchaEnabled && <MTCaptcha captchaId={CAPTCHA_ID} />}
      <Button
        type="primary"
        size="medium"
        actionType="submit"
        disabled={!canSubmit || isSubmitting}
        customClass="ai-sign-in-modal__submit"
      >
        {t(isTwoFaRequired ? 'signIn.password.twoFaVerify' : 'signIn.password.submit')}
      </Button>
      {passwordError && (
        <p className="ai-sign-in-modal__password-error cgt-body-base" aria-live="polite">
          {passwordError}
        </p>
      )}
      <p className="ai-sign-in-modal__back">
        <button
          type="button"
          className="ai-sign-in-modal__switch-link cgt-body-md-semibold cgt-link-primary-accented"
          onClick={onBack}
        >
          {t('signIn.password.back')}
        </button>
      </p>
    </form>
  )
}

PasswordStep.propTypes = {
  email: string.isRequired,
  password: string.isRequired,
  isSubmitting: bool.isRequired,
  isTwoFaRequired: bool.isRequired,
  twoFaCode: string.isRequired,
  captchaEnabled: bool.isRequired,
  passwordError: string,
  showRequiredNotice: bool,
  onEmailChange: func.isRequired,
  onPasswordChange: func.isRequired,
  onTwoFaCodeChange: func.isRequired,
  onSubmit: func.isRequired,
  onBack: func.isRequired,
}

export default PasswordStep
